import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';
import { MatrixCellType } from 'app/b2b-order/components/order/matrix/cell/cell.component';
import { GroupedHeader } from 'app/b2b-order/components/order/matrix/matrix-headers.component';
import { MatrixConfig } from 'app/b2b-order/components/order/matrix/matrix.component';
import { DataSource, InteractiveCell, ReadyOnlyCell } from 'app/b2b-order/components/order/types';
import { LanguageService } from 'shared/services';
import { ArticleSource, Category, SizePrefixMap, StandardizedProduct } from './article-matrix.types';
import { standardizeArticle } from './article-matrix-mappers';
import { ArticleDetails } from '../../components/smart-replenishment/articles/article-details';
import { ArticleCard, ArticleCardV4 } from '../../components/smart-replenishment';
import { take } from 'rxjs/operators';
import { Period } from 'app/shared/services/product-search/constants';
import { isTwoDimensionArticle } from 'app/smart-replenishment/helpers/size-matrix';
import { DEFAULT_REPLENISHMENT_PERIOD } from 'app/smart-replenishment/helpers/period-to-date-range';
import { getBrandStockTooltipText } from 'app/order-shared/helpers';
import { extractNumericFromSize, sizeComparator, sortSizes } from 'app/smart-replenishment/helpers/size-matrix';

export { extractNumericFromSize, sizeComparator, sortSizes };

enum TranslationKeys {
    RetailerStock = 'ORDER_AUTO_RESTOCK.LABEL.MANUFACTURER.RETAILER_STOCK',
    TargetStock = 'ORDER_AUTO_RESTOCK.LABEL.TARGET_STOCK',
    SoldLast30Days = 'ORDER_AUTO_RESTOCK.LABEL.SOLD_LAST_30_DAYS',
    BackorderTextMultiWeek = 'SMART_REPLENISHMENT_AUTO_RESTOCK.BACKORDER_TEXT_MULTI_WEEK',
    BackorderTextMultiWeekSingular = 'SMART_REPLENISHMENT_AUTO_RESTOCK.BACKORDER_TEXT_MULTI_WEEK_SINGULAR',
    AvailableText = 'SMART_REPLENISHMENT_AUTO_RESTOCK.AVAILABLE_TEXT',
    TargetQty = 'REPLENISHMENT.METRICS.TARGET_QTY',
    GridQuantityLabel = 'ORDER_SUGGESTIONS.LABEL.SUGGESTED_QUANTITY',
    GridSoldLabel = 'ORDER_SUGGESTIONS.LABEL.SOLD_QUANTITY',
    SoldYesterday = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_YESTERDAY',
    Sold7Days = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_7_DAYS',
    Sold30Days = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_30_DAYS',
    Sold12Months = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_12_MONTHS',
    SoldLastWeek = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_LAST_WEEK',
    SoldLastMonth = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_LAST_MONTH',
    SoldLastYear = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_LAST_YEAR',
    SoldAllTime = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_ALL_TIME',
    SoldThisWeek = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_THIS_WEEK',
    SoldThisMonth = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_THIS_MONTH',
    SoldThisYear = 'SMART_REPLENISHMENT.ADD_ARTICLE.SOLD_THIS_YEAR'
}

const PERIOD_TO_SOLD_LABEL_TRANSLATION_KEY_MAP: { [key in Period]: TranslationKeys } = {
    [Period.YESTERDAY]: TranslationKeys.SoldYesterday,
    [Period.LAST_7_DAYS]: TranslationKeys.Sold7Days,
    [Period.LAST_30_DAYS]: TranslationKeys.Sold30Days,
    [Period.LAST_12_MONTHS]: TranslationKeys.Sold12Months,
    [Period.PREVIOUS_CALENDAR_WEEK]: TranslationKeys.SoldLastWeek,
    [Period.PREVIOUS_CALENDAR_MONTH]: TranslationKeys.SoldLastMonth,
    [Period.PREVIOUS_CALENDAR_YEAR]: TranslationKeys.SoldLastYear,
    [Period.ALL_PAST_DATES]: TranslationKeys.SoldAllTime,
    [Period.CURRENT_WEEK]: TranslationKeys.SoldThisWeek,
    [Period.CURRENT_MONTH]: TranslationKeys.SoldThisMonth,
    [Period.CURRENT_YEAR]: TranslationKeys.SoldThisYear
};

@Injectable({
    providedIn: 'root'
})
export class ArticleMatrixService {
    translationMap: Record<TranslationKeys, string>;

    constructor(private languageService: LanguageService) {
        if (!_.isEmpty(this.translationMap)) {
            return;
        }

        this.languageService
            .translateObservable(Object.values(TranslationKeys))
            .pipe(take(1))
            .subscribe((translationMap) => {
                this.translationMap = translationMap as Record<TranslationKeys, string>;
            });
    }

    initializeMatrix(
        mode: ArticleSource,
        article: ArticleDetails | ArticleCardV4 | ArticleCard,
        storeGln?: string,
        isSuggestions = false,
        isViewOnlyMatrix = false,
        period: Period = DEFAULT_REPLENISHMENT_PERIOD,
        disabled = false
    ): {
        config: MatrixConfig;
        dataSource: DataSource;
        cellIdToGtinMap: Record<string, string>;
        gtinToSizeMap: Record<string, string>;
    } {
        if (!article) {
            return;
        }

        const standardizedArticle = standardizeArticle(mode, article, storeGln);
        const products = standardizedArticle.products.sort((a, b) => {
            return sizeComparator(a.size, b.size);
        });

        // TODO: What's up with populating with MatrixData??? Will need to add that is required.
        if (!products.length) {
            return;
        }

        const category = standardizedArticle.category;
        const swapApplicableCategories = [Category.Pants, Category.DenimPants, Category.Jumpsuits];
        const sizes: string[] = products.map((product) => product.size);
        const useTwoDimensions: boolean = isTwoDimensionArticle(sizes);

        if (!sizes.length) {
            return;
        }

        const config = this.getDefaultMatrixConfig();
        const swapHeaders = swapApplicableCategories.includes(category) && useTwoDimensions;

        if (!useTwoDimensions) {
            config.groupedHeaders = undefined;
            config.useGroupedHeaders = false;
        } else {
            const headerPrefixes = this.calculateGroupedHeadersPrefixes(swapHeaders);

            config.groupedHeaders = this.generateGroupedHeaders(sizes, swapHeaders).map((headers) => ({
                ...headers,
                ...headerPrefixes
            }));

            config.invertGroupedHeaders = swapHeaders;
            config.useGroupedHeaders = true;
        }

        if (isViewOnlyMatrix) {
            config.hideFirstCell = true;
            config.skipEmptyLeftHeaders = true;
        }

        const dataSource = this.generateDataSource(
            mode,
            isSuggestions,
            products,
            useTwoDimensions,
            swapHeaders,
            isViewOnlyMatrix,
            period,
            disabled
        );

        // Reverse lookup from GTIN to standardized size, used by the clipboard
        // to resolve each cell's size independently of its position in the data source.
        const gtinToSizeMap: Record<string, string> = {};
        products.forEach((product) => {
            gtinToSizeMap[product.gtin] = product.size;
        });

        return {
            config,
            dataSource,
            cellIdToGtinMap: this.generateCellIdToGtinMap(dataSource, isViewOnlyMatrix),
            gtinToSizeMap
        };
    }

    initializeGridMatrix(
        mode: ArticleSource,
        article: ArticleDetails | ArticleCardV4 | ArticleCard,
        options?: {
            storeGln?: string;
            isSuggestions?: boolean;
            disabled?: boolean;
            enableIconTooltip?: boolean;
        }
    ): { dataSource: DataSource } | undefined {
        if (!article) {
            return;
        }

        const standardizedArticle = standardizeArticle(mode, article, options?.storeGln);
        const products = standardizedArticle.products.sort((a, b) => sizeComparator(a.size, b.size));

        if (!products.length) {
            return;
        }

        const dataSource = this.generateGridDataSource(
            products,
            options?.isSuggestions ?? false,
            options?.disabled ?? false,
            options?.enableIconTooltip ?? true
        );

        return { dataSource };
    }

    private generateGridDataSource(
        products: StandardizedProduct[],
        isSuggestions: boolean,
        disabled: boolean,
        enableIconTooltip: boolean
    ): DataSource {
        const validatorGenerator = this.generateValidators(ArticleSource.Details, isSuggestions);

        return products.map((product) => {
            const iconTooltipLabels = enableIconTooltip
                ? {
                      available: this.translationMap[TranslationKeys.AvailableText],
                      backorder: this.translationMap[TranslationKeys.BackorderTextMultiWeek],
                      backorderSingular: this.translationMap[TranslationKeys.BackorderTextMultiWeekSingular]
                  }
                : undefined;

            const interactiveCell: InteractiveCell = {
                id: product.gtin,
                leftLabel: product.size,
                topLabel: this.translationMap[TranslationKeys.GridQuantityLabel],
                selectedQuantity: product.selectedQuantity,
                availableQuantity: product.availableQuantity,
                isAvailableQuantityVisible: true,
                validators: validatorGenerator(product),
                disabled: disabled || (isSuggestions && !product.selectedQuantity && !product.availableQuantity),
                iconTooltip:
                    product.brandStock && iconTooltipLabels
                        ? getBrandStockTooltipText(product.brandStock, iconTooltipLabels)
                        : undefined
            };

            const retailerStockCell: ReadyOnlyCell = {
                items: [{ quantity: product.retailerStock ?? 0, text: String(product.retailerStock ?? 0) }],
                topLabel: this.translationMap[TranslationKeys.RetailerStock],
                cellType: MatrixCellType.BodyText,
                hideCount: true
            };

            const soldCell: ReadyOnlyCell = {
                items: [{ quantity: product.soldQuantity ?? 0, text: String(product.soldQuantity ?? 0) }],
                topLabel: this.translationMap[TranslationKeys.GridSoldLabel],
                cellType: MatrixCellType.BodyText,
                hideCount: true
            };

            return [interactiveCell, retailerStockCell, soldCell];
        });
    }

    private generateCellIdToGtinMap(dataSource: DataSource, isViewOnlyMatrix: boolean): Record<string, string> {
        const cellIdToGtinMap: Record<string, string> = {};
        const firstRow = dataSource[0];

        if (!firstRow || !firstRow.length || isViewOnlyMatrix) {
            return {};
        }

        firstRow.forEach((cell) => {
            if ('id' in cell && 'hoverText' in cell) {
                cellIdToGtinMap[cell.id] = cell.hoverText;
            }
        });

        return cellIdToGtinMap;
    }

    private generateReadOnlyRowsConfig(
        mode: ArticleSource,
        isViewOnlyMatrix: boolean,
        period: Period
    ): { label: string; path: keyof StandardizedProduct }[] {
        const configMap = {
            [ArticleSource.Details]: [
                { label: this.translationMap[TranslationKeys.RetailerStock], path: 'retailerStock' },
                { label: this.translationMap[PERIOD_TO_SOLD_LABEL_TRANSLATION_KEY_MAP[period]], path: 'soldQuantity' }
            ],
            [ArticleSource.CardV4]: [{ label: null, path: 'selectedQuantity' }],
            [ArticleSource.CardLegacy]: isViewOnlyMatrix
                ? [{ label: null, path: 'selectedQuantity' }]
                : [
                      { label: this.translationMap[TranslationKeys.RetailerStock], path: 'retailerStock' },
                      {
                          label: this.translationMap[PERIOD_TO_SOLD_LABEL_TRANSLATION_KEY_MAP[period]],
                          path: 'soldQuantity'
                      }
                  ]
        };

        return configMap[mode] || [];
    }

    private generateValidators(mode: ArticleSource, useMaxValidator: boolean) {
        if (mode === ArticleSource.Details) {
            return (product: StandardizedProduct) => [
                Validators.min(0),
                useMaxValidator ? Validators.max(product.availableQuantity) : null
            ];
        }

        return (product: StandardizedProduct) => [Validators.min(0)];
    }

    private generateGroupedHeaders(sizes: string[], swapHeaders: boolean) {
        const parentSizeHeader = new Set<string>();
        const sizeMap: { [waist: string]: string[] } = {};

        sizes.forEach((size) => {
            if (size.includes('/')) {
                const splitSize = size.split('/');
                const [parentSize, childSize] = swapHeaders ? splitSize.reverse() : splitSize;

                parentSizeHeader.add(parentSize);

                if (!sizeMap[parentSize]) {
                    sizeMap[parentSize] = [];
                }

                sizeMap[parentSize].push(childSize);
            }
        });

        const sortedParentSizeHeaders = sortSizes(Array.from(parentSizeHeader));

        const groupedHeaders: GroupedHeader[] = sortedParentSizeHeaders.map((parentSize) => ({
            parentLabel: parentSize,
            childLabels: sortSizes(sizeMap[parentSize])
        }));

        return groupedHeaders;
    }

    private calculateGroupedHeadersPrefixes(swapHeaders?: boolean) {
        if (swapHeaders) {
            return {
                parentPrefix: SizePrefixMap.length,
                childPrefix: SizePrefixMap.width
            };
        }

        return {
            parentPrefix: '',
            childPrefix: ''
        };
    }

    private generateDataSource(
        mode: ArticleSource,
        isSuggestions: boolean,
        products: StandardizedProduct[],
        useTwoDimensions: boolean,
        swapHeaders: boolean,
        isViewOnlyMatrix: boolean,
        period: Period,
        disabled: boolean
    ) {
        const readOnlyRowsConfig = this.generateReadOnlyRowsConfig(mode, isViewOnlyMatrix, period);
        const validatorGenerator = this.generateValidators(mode, isSuggestions);

        const qtyRow = this.generateQtyRow(
            products,
            useTwoDimensions,
            swapHeaders,
            validatorGenerator,
            isSuggestions,
            disabled
        );

        const readOnlyRows = readOnlyRowsConfig.map((config) =>
            this.generateReadOnlyRow(products, config.label, config.path, swapHeaders)
        );

        return isViewOnlyMatrix ? [...readOnlyRows] : [qtyRow, ...readOnlyRows];
    }

    private generateQtyRow(
        products: StandardizedProduct[],
        useTwoDimensions: boolean,
        swapHeaders: boolean,
        getValidators: (product: StandardizedProduct) => ValidatorFn[],
        isSuggestions: boolean,
        disabled: boolean
    ): Array<InteractiveCell | ReadyOnlyCell> {
        return products.map((product) => {
            let id = `qty-${product.size}`;
            let topLabel = product.size;

            if (useTwoDimensions) {
                const [width, length] = product.size.split('/');

                if (swapHeaders) {
                    id = `qty-${length}/${width}`;
                    topLabel = length;
                } else {
                    id = `qty-${width}/${length}`;
                    topLabel = width;
                }
            }

            const iconToolTipLabels = this.getIconTooltipLabels();

            return {
                id,
                topLabel: topLabel,
                leftLabel: this.translationMap[TranslationKeys.TargetQty],
                hoverText: product.gtin,
                selectedQuantity: product.selectedQuantity,
                availableQuantity: product.availableQuantity,
                isAvailableQuantityVisible: true,
                validators: getValidators(product),
                disabled: disabled || (isSuggestions ? !product.selectedQuantity && !product.availableQuantity : false),
                iconTooltip: product.brandStock
                    ? getBrandStockTooltipText(product.brandStock, iconToolTipLabels)
                    : undefined
            } as InteractiveCell;
        });
    }

    private generateReadOnlyRow(
        products: StandardizedProduct[],
        label: string,
        path: keyof StandardizedProduct,
        swapHeaders: boolean
    ): Array<ReadyOnlyCell> {
        return products.map((product) => {
            const topLabel = swapHeaders ? product.size.split('/').reverse().join('/') : product.size;

            const iconToolTipLabels = this.getIconTooltipLabels();
            const shouldShowBackorderIcon = path === 'selectedQuantity';

            return {
                topLabel,
                leftLabel: label,
                hoverText: product.gtin,
                cellType: MatrixCellType.BodyText,
                items: [
                    {
                        text: String(product[path])
                    }
                ],
                iconTooltip:
                    shouldShowBackorderIcon && product.brandStock && product.brandStock.backorder?.available > 0
                        ? getBrandStockTooltipText(product.brandStock, iconToolTipLabels)
                        : undefined
            };
        });
    }

    private getIconTooltipLabels() {
        return {
            available: this.translationMap[TranslationKeys.AvailableText],
            backorder: this.translationMap[TranslationKeys.BackorderTextMultiWeek],
            backorderSingular: this.translationMap[TranslationKeys.BackorderTextMultiWeekSingular]
        };
    }

    /**
     * Get default matrix configuration
     */
    private getDefaultMatrixConfig(): MatrixConfig {
        return {
            showHeader: true,
            showFooter: false,
            capitalizeHeaders: true,
            capitalizeLeftHeaders: false,
            filterDataSource: false,
            monitorStatusChanges: true,
            useTooltip: true,
            sizePrefix: false,
            showCopy: false,
            useGroupedHeaders: false,
            groupedHeaders: undefined,
            zebraStyle: true,
            hideFirstCell: false,
            skipEmptyLeftHeaders: false,
            enableHoverAndCopy: true,
            useStickyHeader: false
        };
    }
}
